import { useState } from 'react'
import { Button, Modal, useTheme } from 'carbonarc-ui'
import { toast } from 'react-hot-toast'
import { Trans, useTranslation } from 'react-i18next'
import { UseQueryResult } from 'react-query'
import { useApi } from '@services/api'
import { useBookmarkStore } from '@stores/bookmarkStore'
import { Bookmark } from '@stores/bookmarkStore/utils'
import { cn } from '@utils/className'

type BookmarkModalSaveProps = {
  bookmark: Bookmark | null
  remoteBookmark: UseQueryResult<Bookmark, unknown>
  show: boolean
  setShow: (show: boolean) => void
  onClose: () => void
  setAlreadyFavorite: (alreadyFavorite: boolean) => void
}

export const BookmarkModalSave = ({
  bookmark,
  remoteBookmark,
  show,
  onClose,
  setAlreadyFavorite,
}: BookmarkModalSaveProps) => {
  const modalTheme = useTheme().theme.modal
  const { t } = useTranslation()
  const [saving, setSaving] = useState(false)
  const { fetch: apiFetch, post: apiPost } = useApi()
  const { refetchAll } = useBookmarkStore((state) => ({ refetchAll: state.fetchBookmarks }))

  const saveFavorite = async () => {
    try {
      setSaving(true)
      await apiPost('/app/userbookmarks', bookmark)
      toast.success(
        <Trans
          i18nKey="bookmark.save.success_message"
          values={{ reportName: bookmark?.name }}
          components={[<span className="font-semibold inline"></span>]}
        />,
      )
      remoteBookmark.refetch()
      setAlreadyFavorite(true)
      refetchAll(apiFetch)
    } catch (e) {
      toast.error(t('bookmark.save.error_message'))
    } finally {
      setSaving(false)
      onClose()
    }
  }

  return (
    <Modal
      show={show}
      position="top-center"
      onClose={() => onClose()}
      theme={{
        root: {
          positions: {
            'top-center': cn(modalTheme.root.positions['top-center'], 'pt-[100px]'),
          },
        },
      }}
    >
      <Modal.Header
        theme={{
          title: cn(modalTheme.header.title, 'text-lg font-semibold'),
        }}
      >
        {t('bookmark.save.modal_title')}
      </Modal.Header>
      <Modal.Body>
        <Trans
          i18nKey="bookmark.save.modal_message"
          values={{
            reportName: bookmark?.name,
          }}
          components={[
            <div className="text-xs font-normal text-gray-500 dark:text-gray-400 mb-3"></div>,
            <div className="text-sm font-semibold text-gray-700 dark:text-gray-300"></div>,
          ]}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="default" onClick={saveFavorite} isLoading={saving}>
          {t('bookmark.save.modal_confirm')}
        </Button>
        <Button variant="outline" onClick={onClose} disabled={saving}>
          {t('bookmark.save.modal_cancel')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
